import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { api } from '../../../utils/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const FormContainer = styled.div`
  background-color: var(--white, #ffffff);
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 2rem;
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: var(--primary, #7e22ce);
  font-size: 1.75rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-light, #94a3b8);
  font-size: 1rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-dark, #1e293b);
  font-size: 0.875rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: var(--primary, #7e22ce);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: var(--primary, #7e22ce);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary, #7e22ce);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: var(--primary-dark, #6b21a8);
  }

  &:disabled {
    background-color: var(--text-light, #94a3b8);
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background-color: var(--border, #e2e8f0);
    z-index: 1;
  }
`;

const Step = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${props => 
    props.$completed ? 'var(--primary, #7e22ce)' : 
    props.$active ? 'var(--primary-light, #a78bfa)' : 
    'var(--white, #ffffff)'};
  border: 2px solid ${props => 
    props.$completed || props.$active ? 'var(--primary, #7e22ce)' : 'var(--border, #e2e8f0)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => 
    props.$completed || props.$active ? 'var(--white, #ffffff)' : 'var(--text-light, #94a3b8)'};
  font-weight: 500;
  position: relative;
  z-index: 2;
`;

const TeacherRegistration: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [otp, setOTP] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    bio: '',
    expertise: '',
    school: '',
    schoolId: '',
    teacherId: '',
    subjects: '',
    experience: '',
    education: '',
    biometrics: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendOTP = async () => {
    try {
      const response = await api.post('/auth/send-otp', {
        email: formData.email
      });

      setIsOTPSent(true);
    } catch (error) {
      console.error('Error sending OTP:', error);
    }
  };

  const verifyTeacherId = async () => {
    try {
      const response = await api.get(`/teachers/verify-school/${formData.teacherId}?schoolName=${encodeURIComponent(formData.school)}`);
      const data = response.data;
      
      if (!response.ok) {
        setVerificationError(data.message || 'Failed to verify teacher ID');
        return false;
      }

      setVerificationError('');
      return true;
    } catch (error) {
      console.error('Error verifying teacher ID:', error);
      setVerificationError('Error verifying teacher ID');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentStep < 3) {
      if (currentStep === 2 && formData.teacherId) {
        const isValid = await verifyTeacherId();
        if (!isValid) {
          return;
        }
      }
      setCurrentStep(prev => prev + 1);
      return;
    }

    try {
      if (!isOTPSent) {
        await handleSendOTP();
        return;
      }

      if (isOTPSent && !otp) {
        alert('Please enter the OTP sent to your email');
        return;
      }

      const verifyResponse = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: formData.email,
          otp 
        }),
      });

      if (!verifyResponse.ok) {
        throw new Error('Invalid OTP');
      }

      await register({
        firstName: formData.name.split(' ')[0],
        lastName: formData.name.split(' ').slice(1).join(' '),
        email: formData.email,
        password: formData.password,
        role: 'teacher',
        schoolId: formData.schoolId,
        teacherId: formData.teacherId,
        bio: formData.bio,
        expertise: formData.expertise,
        biometrics: {
          rawData: formData.biometrics || `BIO-${Date.now()}`
        }
      });
      navigate('/auth/login');
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            {isOTPSent && (
              <FormGroup>
                <Label>Enter OTP</Label>
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => setOTP(e.target.value)}
                  placeholder="Enter the OTP sent to your email"
                  required
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label>Password</Label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </>
        );
      case 2:
        return (
          <>
            <FormGroup>
              <Label>Bio</Label>
              <TextArea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell us about yourself..."
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Areas of Expertise</Label>
              <TextArea
                name="expertise"
                value={formData.expertise}
                onChange={handleChange}
                placeholder="List your areas of expertise..."
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>School/Institution</Label>
              <Input
                type="text"
                name="school"
                value={formData.school}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>School ID</Label>
              <Input
                type="text"
                name="schoolId"
                value={formData.schoolId}
                onChange={handleChange}
                placeholder="Enter your school ID (e.g., SCH-0009)"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Teacher ID</Label>
              <Input
                type="text"
                name="teacherId"
                value={formData.teacherId}
                onChange={handleChange}
                placeholder="Enter your teacher ID (e.g., PON-25-09)"
                required
              />
              {verificationError && (
                <p style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {verificationError}
                </p>
              )}
            </FormGroup>
          </>
        );
      case 3:
        return (
          <>
            <FormGroup>
              <Label>Subjects You Teach</Label>
              <TextArea
                name="subjects"
                value={formData.subjects}
                onChange={handleChange}
                placeholder="List the subjects you teach..."
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Years of Experience</Label>
              <Input
                type="text"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Education</Label>
              <TextArea
                name="education"
                value={formData.education}
                onChange={handleChange}
                placeholder="List your educational background..."
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Biometrics</Label>
              <Input
                type="text"
                name="biometrics"
                value={formData.biometrics}
                onChange={handleChange}
                placeholder="Enter biometric data (if available)"
              />
            </FormGroup>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <FormContainer>
        <Title>Teacher Registration</Title>
        <Subtitle>Step {currentStep} of 3</Subtitle>
        
        <ProgressBar>
          <Step $active={currentStep === 1} $completed={currentStep > 1}>1</Step>
          <Step $active={currentStep === 2} $completed={currentStep > 2}>2</Step>
          <Step $active={currentStep === 3} $completed={currentStep > 3}>3</Step>
        </ProgressBar>

        <Form onSubmit={handleSubmit}>
          {renderStep()}
          <Button type="submit">
            {currentStep === 3 ? 'Complete Registration' : 'Next Step'}
          </Button>
          {currentStep === 1 && !isOTPSent && (
            <Button type="button" onClick={handleSendOTP} style={{ marginTop: '1rem' }}>
              Send OTP
            </Button>
          )}
        </Form>
      </FormContainer>
    </Container>
  );
};

export default TeacherRegistration; 